// src/components/dashboards/LabBilling.jsx
// UPDATED: Added discount support — Billing Member can apply discounts when generating bills.
// Billing Manager has a richer portal (BillingManager.jsx); this is the day-to-day
// billing workflow for BILLING_MEMBER.
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import { labAPI } from '../../services/api';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  pageBg: '#f0f4f8', surface: '#ffffff', surfaceAlt: '#f8fafc',
  primary: '#0369a1', primaryLight: '#e0f2fe', primaryDark: '#075985',
  teal: '#0d9488', tealLight: '#f0fdfa', tealDark: '#0f766e',
  green: '#16a34a', greenLight: '#f0fdf4', greenMid: '#dcfce7',
  orange: '#ea580c', orangeLight: '#fff7ed', orangeMid: '#fed7aa',
  red: '#dc2626', redLight: '#fef2f2', redMid: '#fecaca',
  blue: '#2563eb', blueLight: '#eff6ff', blueMid: '#dbeafe',
  yellow: '#ca8a04', yellowLight: '#fefce8', yellowMid: '#fef08a',
  slate50: '#f8fafc', slate100: '#f1f5f9', slate200: '#e2e8f0',
  slate400: '#94a3b8', slate500: '#64748b', slate600: '#475569',
  slate700: '#334155', slate800: '#1e293b', slate900: '#0f172a',
  textPrimary: '#0f172a', textSecondary: '#475569', textMuted: '#94a3b8',
  mono: "'JetBrains Mono', 'Courier New', monospace",
};

const STATUS_META = {
  ORDERED:          { label: 'Ordered',          bg: C.slate100,   color: C.slate600,  dot: '#94a3b8' },
  BILLED:           { label: 'Bill Generated',   bg: C.blueMid,    color: C.blue,      dot: '#2563eb' },
  PAID:             { label: 'Paid',             bg: C.greenMid,   color: C.green,     dot: '#16a34a' },
  SAMPLE_COLLECTED: { label: 'Sample Collected', bg: C.orangeMid,  color: C.orange,    dot: '#ea580c' },
  IN_PROGRESS:      { label: 'In Progress',      bg: C.blueMid,    color: C.blue,      dot: '#2563eb' },
  COMPLETED:        { label: 'Completed',        bg: C.greenMid,   color: C.green,     dot: '#16a34a' },
  CANCELLED:        { label: 'Cancelled',        bg: C.redMid,     color: C.red,       dot: '#dc2626' },
};

const CATEGORY_COLORS = {
  Microbiology: { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
  Pathology:    { bg: '#ede9fe', color: '#5b21b6', border: '#c4b5fd' },
  Biochemistry: { bg: '#e0f2fe', color: '#0369a1', border: '#7dd3fc' },
};

const injectStyles = () => {
  if (document.getElementById('lab-billing-styles')) return;
  const style = document.createElement('style');
  style.id = 'lab-billing-styles';
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
    .lb-root * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }
    .lb-input { width:100%; padding:9px 12px; border:1.5px solid #e2e8f0; border-radius:8px;
      font-size:14px; outline:none; color:#0f172a; background:#fff; transition:border-color .15s; }
    .lb-input:focus { border-color:#0284c7; box-shadow:0 0 0 3px rgba(3,105,161,.1); }
    .lb-select { padding:9px 12px; border:1.5px solid #e2e8f0; border-radius:8px;
      font-size:13px; color:#0f172a; background:#fff; outline:none; cursor:pointer; }
    .lb-btn { padding:9px 18px; border:none; border-radius:8px; font-size:13px;
      font-weight:700; cursor:pointer; display:flex; align-items:center; gap:6px; transition:all .15s; }
    .lb-btn:disabled { opacity:.6; cursor:not-allowed; }
    .lb-btn-primary { background:linear-gradient(135deg,#0284c7,#0369a1); color:#fff;
      box-shadow:0 2px 8px rgba(3,105,161,.3); }
    .lb-btn-primary:hover:not(:disabled) { filter:brightness(1.08); transform:translateY(-1px); }
    .lb-btn-success { background:linear-gradient(135deg,#16a34a,#15803d); color:#fff; }
    .lb-btn-success:hover:not(:disabled) { filter:brightness(1.08); }
    .lb-btn-outline { background:#fff; border:1.5px solid #e2e8f0; color:#475569; }
    .lb-btn-outline:hover { background:#f8fafc; }
    .lb-tr:hover td { background:#f1f7ff !important; }
    .lb-modal-overlay { position:fixed; inset:0; background:rgba(15,23,42,.55);
      display:flex; align-items:center; justify-content:center; z-index:1000;
      backdrop-filter:blur(3px); animation:lbFadeIn .15s ease; }
    .lb-modal { background:#fff; border-radius:16px;
      box-shadow:0 32px 80px rgba(15,23,42,.25); animation:lbSlideUp .2s ease; }
    .lb-tab-btn { flex:1; padding:10px 0; font-weight:700; font-size:13px; border:none;
      border-radius:8px; cursor:pointer; transition:all .2s; }
    .lb-tab-btn.active { background:#fff; color:#0369a1;
      box-shadow:0 2px 8px rgba(3,105,161,.15); }
    .lb-tab-btn.inactive { background:transparent; color:#64748b; }
    .lb-tab-btn.inactive:hover { background:rgba(255,255,255,.5); }
    @keyframes lbFadeIn { from{opacity:0} to{opacity:1} }
    @keyframes lbSlideUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
    ::-webkit-scrollbar{width:6px} ::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:99px}
  `;
  document.head.appendChild(style);
};

const Badge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, bg: C.slate100, color: C.slate600, dot: '#94a3b8' };
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5,
      background:m.bg, color:m.color, padding:'3px 10px', borderRadius:99,
      fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:m.dot }} />
      {m.label}
    </span>
  );
};

const CategoryTag = ({ name }) => {
  const cfg = CATEGORY_COLORS[name] || { bg:'#f1f5f9', color:'#475569', border:'#e2e8f0' };
  return (
    <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:6,
      background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.border}` }}>
      {name}
    </span>
  );
};

const StatCard = ({ icon, label, value, color }) => (
  <div style={{ background:'#fff', borderRadius:12, padding:20,
    border:'1px solid #e2e8f0', boxShadow:'0 1px 3px rgba(0,0,0,.06)' }}>
    <div style={{ width:40, height:40, borderRadius:10, background:color+'18',
      display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, marginBottom:12 }}>
      {icon}
    </div>
    <div style={{ fontSize:26, fontWeight:800, color:C.textPrimary, lineHeight:1, marginBottom:4 }}>{value}</div>
    <div style={{ fontSize:13, fontWeight:600, color:C.textSecondary }}>{label}</div>
  </div>
);

const thS = { padding:'11px 16px', textAlign:'left', background:C.slate50,
  color:C.slate500, fontWeight:700, fontSize:11, textTransform:'uppercase',
  letterSpacing:'.06em', borderBottom:`2px solid ${C.slate200}` };
const tdS = { padding:'13px 16px', borderBottom:`1px solid ${C.slate100}`,
  color:C.textPrimary, verticalAlign:'middle' };

// ─── Invoice Print / Download ─────────────────────────────────────────────────
const printInvoice = (order) => {
  const finalAmt    = parseFloat(order.final_amount  || order.bill_amount || 0);
  const billAmt     = parseFloat(order.bill_amount   || 0);
  const discountAmt = parseFloat(order.discount_amount || 0);
  const hasDiscount = discountAmt > 0;
  const fmt         = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  const nowStr      = new Date().toLocaleString('en-IN', {
    day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit'
  });

  // Safely resolve patient name — handles null, undefined, and "null" string
  const patientName = (() => {
    const raw = order.patient_name;
    if (!raw || raw === 'null' || raw === 'undefined') return '—';
    return raw;
  })();

  // Safely resolve item price across all serializer shapes:
  // LabTestOrderListSerializer → item.test_price (DecimalField)
  // LabTestOrderDetailSerializer (LabTestItemWithParamsSerializer) → no test_price
  // So fall back to item.test?.price, then item.price, then 0
  const getItemPrice = (item) => {
    const candidates = [item.test_price, item.test?.price, item.price];
    for (const c of candidates) {
      const n = parseFloat(c);
      if (!isNaN(n) && n > 0) return n;
    }
    return 0;
  };

  const itemRows = (order.items || []).map((item, i) => `
    <tr>
      <td>${i + 1}</td>
      <td>${item.test_name || '—'}</td>
      <td>${item.category_name || '—'}</td>
      <td>${item.sample_type || item.sample_type_display || '—'}</td>
      <td class="amt">${fmt(getItemPrice(item))}</td>
    </tr>`).join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Invoice — ${patientName}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&family=Roboto+Mono:wght@700&display=swap');
    * { box-sizing:border-box; margin:0; padding:0; }
    body { font-family:'Plus Jakarta Sans',sans-serif; background:#fff; color:#0f172a;
           padding:36px 48px; font-size:13px; }

    /* ── Header ── */
    .hdr { display:flex; justify-content:space-between; align-items:flex-start;
           padding-bottom:22px; border-bottom:2px solid #0369a1; margin-bottom:26px; }
    .hdr-logo { display:flex; align-items:center; gap:12px; }
    .hdr-icon { width:48px; height:48px; border-radius:12px;
                background:linear-gradient(135deg,#0284c7,#0d9488);
                display:flex; align-items:center; justify-content:center;
                font-size:24px; color:#fff; }
    .hdr-title h1 { font-size:20px; font-weight:800; color:#0f172a; }
    .hdr-title p  { font-size:11px; color:#64748b; margin-top:2px; }
    .hdr-meta     { text-align:right; font-size:11px; color:#64748b; line-height:1.9; }
    .hdr-meta strong { color:#0f172a; }
    .inv-badge    { display:inline-block; background:#0369a1; color:#fff;
                    font-size:13px; font-weight:800; padding:4px 14px;
                    border-radius:6px; margin-bottom:6px; }

    /* ── Patient block ── */
    .patient-block { display:flex; gap:20px; background:#f0f9ff;
                     border:1.5px solid #bae6fd; border-radius:10px;
                     padding:16px 20px; margin-bottom:24px; }
    .patient-block .section { flex:1; }
    .section h3 { font-size:10px; font-weight:800; color:#0369a1;
                  text-transform:uppercase; letter-spacing:.07em; margin-bottom:8px; }
    .row { display:flex; gap:6px; font-size:12px; margin-bottom:4px; }
    .row .lbl { color:#64748b; min-width:90px; }
    .row .val { font-weight:700; color:#0f172a; }

    /* ── Items table ── */
    h2 { font-size:12px; font-weight:800; text-transform:uppercase; letter-spacing:.07em;
         color:#0f172a; margin-bottom:10px; }
    table { width:100%; border-collapse:collapse; margin-bottom:22px; }
    thead th { background:#f1f5f9; color:#64748b; font-size:10px; font-weight:800;
               text-transform:uppercase; letter-spacing:.07em;
               padding:9px 12px; border-bottom:1.5px solid #e2e8f0; text-align:left; }
    thead th.amt { text-align:right; }
    tbody td { padding:10px 12px; border-bottom:1px solid #f1f5f9;
               font-size:12px; color:#0f172a; vertical-align:middle; }
    tbody td.amt { text-align:right; font-family:'Roboto Mono',monospace; font-weight:700; }
    tbody tr:nth-child(even) td { background:#fafbfe; }

    /* ── Totals ── */
    .totals { width:320px; margin-left:auto; border:1.5px solid #e2e8f0; border-radius:10px; overflow:hidden; }
    .tot-row { display:flex; justify-content:space-between;
               padding:9px 16px; font-size:13px; }
    .tot-row:nth-child(even) { background:#f8fafc; }
    .tot-row .lbl { color:#64748b; }
    .tot-row .val { font-family:'Roboto Mono',monospace; font-weight:700; }
    .tot-row.discount .val { color:#dc2626; }
    .tot-row.final { background:#0369a1 !important; }
    .tot-row.final .lbl,
    .tot-row.final .val { color:#fff; font-size:15px; font-weight:800; }

    /* ── Payment info ── */
    .pay-block { display:flex; gap:16px; margin-top:22px; }
    .pay-pill  { background:#f0fdf4; border:1.5px solid #bbf7d0; border-radius:8px;
                 padding:10px 16px; flex:1; }
    .pay-pill h4 { font-size:10px; color:#16a34a; font-weight:800;
                   text-transform:uppercase; letter-spacing:.07em; margin-bottom:4px; }
    .pay-pill p  { font-size:13px; font-weight:700; color:#0f172a; }

    /* ── Footer ── */
    .footer { margin-top:30px; padding-top:16px; border-top:1px solid #e2e8f0;
              font-size:11px; color:#94a3b8; display:flex; justify-content:space-between; }

    @media print {
      body { padding:16px 24px; }
      @page { margin:1cm; }
    }
  </style>
</head>
<body>
  <div class="hdr">
    <div class="hdr-logo">
      <div class="hdr-icon">🏥</div>
      <div class="hdr-title">
        <h1>Hospital Lab — Invoice</h1>
        <p>Official Lab Test Bill</p>
      </div>
    </div>
    <div class="hdr-meta">
      <div class="inv-badge">INVOICE #LAB-${String(order.id).padStart(6,'0')}</div>
      <div><strong>Date:</strong> ${nowStr}</div>
      <div><strong>Token:</strong> ${order.token_number || '—'}</div>
      <div><strong>Status:</strong> ${order.status === 'PAID' ? '✅ PAID' : '🧾 BILLED'}</div>
    </div>
  </div>

  <div class="patient-block">
    <div class="section">
      <h3>Patient Details</h3>
      <div class="row"><span class="lbl">Name:</span><span class="val">${patientName}</span></div>
      <div class="row"><span class="lbl">Patient ID:</span><span class="val">${order.patient_id || '—'}</span></div>
      <div class="row"><span class="lbl">Token No:</span><span class="val">${order.token_number || '—'}</span></div>
    </div>
    <div class="section">
      <h3>Order Info</h3>
      <div class="row"><span class="lbl">Order #:</span><span class="val">${order.id}</span></div>
      <div class="row"><span class="lbl">Doctor:</span><span class="val">${order.doctor_name || '—'}</span></div>
      <div class="row"><span class="lbl">Urgency:</span><span class="val">${order.urgency || 'ROUTINE'}</span></div>
      <div class="row"><span class="lbl">Order Date:</span><span class="val">
        ${order.order_date ? new Date(order.order_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'}
      </span></div>
    </div>
  </div>

  <h2>Test Details</h2>
  <table>
    <thead>
      <tr>
        <th style="width:36px">#</th>
        <th>Test Name</th>
        <th>Category</th>
        <th>Sample Type</th>
        <th class="amt">Amount</th>
      </tr>
    </thead>
    <tbody>${itemRows}</tbody>
  </table>

  <div class="totals">
    <div class="tot-row">
      <span class="lbl">Subtotal</span>
      <span class="val">${fmt(billAmt)}</span>
    </div>
    ${hasDiscount ? `
    <div class="tot-row discount">
      <span class="lbl">Discount${order.discount_reason ? ` (${order.discount_reason})` : ''}</span>
      <span class="val">− ${fmt(discountAmt)}</span>
    </div>` : ''}
    <div class="tot-row final">
      <span class="lbl">${hasDiscount ? 'Final Amount' : 'Total Amount'}</span>
      <span class="val">${fmt(finalAmt)}</span>
    </div>
  </div>

  ${order.payment_method ? `
  <div class="pay-block">
    <div class="pay-pill">
      <h4>Payment Method</h4>
      <p>${order.payment_method}</p>
    </div>
    ${order.payment_confirmed_at ? `
    <div class="pay-pill">
      <h4>Payment Date</h4>
      <p>${new Date(order.payment_confirmed_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</p>
    </div>` : ''}
  </div>` : ''}

  <div class="footer">
    <span>🏥 Hospital Lab Billing System — Confidential</span>
    <span>Generated: ${nowStr}</span>
  </div>

  <script>window.onload = () => { window.print(); }<\/script>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=860,height=700');
  win.document.write(html);
  win.document.close();
};

const downloadInvoicePDF = (order) => {
  // Opens print dialog which allows "Save as PDF"
  printInvoice(order);
};

// ─── Bill Generation Modal (with discount) ────────────────────────────────────
const BillModal = ({ order, onClose, onSuccess }) => {
  // Resolve item price across LabTestOrderListSerializer (test_price) and
  // LabTestOrderDetailSerializer (LabTestItemWithParamsSerializer, no test_price)
  const resolvePrice = (item) => {
    for (const v of [item.test_price, item.test?.price, item.price]) {
      const n = parseFloat(v);
      if (!isNaN(n) && n > 0) return n;
    }
    return 0;
  };
  const subtotal = order.items?.reduce((s, i) => s + resolvePrice(i), 0) || 0;
  const [amount,         setAmount]         = useState(order.bill_amount || subtotal.toFixed(2));
  const [discountType,   setDiscountType]   = useState('none');   // 'none' | 'flat' | 'percent'
  const [discountValue,  setDiscountValue]  = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');

  const baseAmount   = parseFloat(amount || 0);
  const discountFlat = discountType === 'flat'
    ? Math.min(parseFloat(discountValue || 0), baseAmount)
    : discountType === 'percent'
      ? (baseAmount * Math.min(parseFloat(discountValue || 0), 100)) / 100
      : 0;
  const finalAmount  = Math.max(0, baseAmount - discountFlat);

  const handleSubmit = async () => {
    if (discountType !== 'none' && parseFloat(discountValue || 0) <= 0) {
      setError('Enter a valid discount value.'); return;
    }
    if (discountType !== 'none' && !discountReason.trim()) {
      setError('Discount reason is required when applying a discount.'); return;
    }
    setLoading(true); setError('');
    try {
      // Step 1: Generate bill with (possibly discounted) final amount
      const payload = { bill_amount: amount };
      const updated = await labAPI.generateBill(order.id, payload);

      // Step 2: If a discount was requested, apply it via the discount endpoint
      if (discountType !== 'none' && discountFlat > 0) {
        await labAPI.applyDiscount(order.id, {
          discount_type:   discountType,
          discount_value:  parseFloat(discountValue),
          discount_reason: discountReason,
          final_amount:    finalAmount,
        });
      }

      // Step 3: Auto-print invoice for the patient
      const orderWithDiscount = {
        ...updated,
        ...(discountType !== 'none' && discountFlat > 0 ? {
          discount_amount: discountFlat,
          discount_reason: discountReason,
          final_amount:    finalAmount,
        } : {}),
      };
      printInvoice(orderWithDiscount);

      onSuccess(updated);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to generate bill');
    } finally { setLoading(false); }
  };

  return (
    <div className="lb-modal-overlay" onClick={onClose}>
      <div className="lb-modal" style={{ width: 500 }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.slate200}`,
          background:'linear-gradient(135deg,#f0fdfa,#e0f2fe)',
          borderRadius:'16px 16px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:C.slate900 }}>🧾 Generate Bill</div>
            <div style={{ fontSize:12, color:C.slate500, marginTop:2 }}>Order #{order.id}</div>
          </div>
          <button onClick={onClose} style={{ background:C.slate100, border:'none',
            width:32, height:32, borderRadius:'50%', cursor:'pointer', fontSize:16, color:C.slate500 }}>✕</button>
        </div>

        <div style={{ padding:24 }}>
          {/* Patient info */}
          <div style={{ background:C.primaryLight, border:'1px solid #bae6fd',
            borderRadius:10, padding:'12px 16px', marginBottom:20 }}>
            <div style={{ fontWeight:800, fontSize:15, color:C.primaryDark }}>{order.patient_name}</div>
            <div style={{ fontSize:12, color:C.primary, marginTop:3 }}>
              ID: {order.patient_id} &nbsp;·&nbsp; Token: <strong>{order.token_number}</strong>
            </div>
          </div>

          {/* Tests summary */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.slate500,
              textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>Tests Ordered</div>
            {order.items?.map((item, i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between',
                padding:'6px 0', borderBottom:`1px solid ${C.slate100}`, fontSize:13 }}>
                <span style={{ color:C.textPrimary }}>{item.test_name}</span>
                <span style={{ fontWeight:700, color:C.tealDark }}>
                  ₹{resolvePrice(item).toFixed(2)}
                </span>
              </div>
            ))}
            <div style={{ display:'flex', justifyContent:'space-between',
              padding:'10px 0 0', fontWeight:800, fontSize:15, color:C.textPrimary }}>
              <span>Subtotal</span>
              <span style={{ color:C.tealDark }}>₹{subtotal.toFixed(2)}</span>
            </div>
          </div>

          {/* Bill amount */}
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12, fontWeight:700, color:C.slate600,
              display:'block', marginBottom:6, textTransform:'uppercase', letterSpacing:'.04em' }}>
              Bill Amount (₹)
            </label>
            <input className="lb-input" type="number" step="0.01"
              value={amount} onChange={e => setAmount(e.target.value)} />
          </div>

          {/* Discount section */}
          <div style={{ background:C.yellowLight, border:`1px solid ${C.yellowMid}`,
            borderRadius:10, padding:'14px 16px', marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.yellow, marginBottom:10,
              textTransform:'uppercase', letterSpacing:'.04em' }}>🏷️ Apply Discount (Optional)</div>

            <div style={{ display:'flex', gap:6, marginBottom:10 }}>
              {['none', 'flat', 'percent'].map(t => (
                <button key={t} type="button"
                  style={{
                    flex:1, padding:'7px 6px', border:`1.5px solid ${discountType===t ? C.yellow : C.slate200}`,
                    background: discountType===t ? C.yellowMid : '#fff',
                    color: discountType===t ? C.yellow : C.slate500,
                    borderRadius:7, cursor:'pointer', fontSize:12, fontWeight:700
                  }}
                  onClick={() => setDiscountType(t)}>
                  {t === 'none' ? 'No Discount' : t === 'flat' ? '₹ Flat' : '% Percent'}
                </button>
              ))}
            </div>

            {discountType !== 'none' && (
              <div style={{ display:'grid', gap:10 }}>
                <div>
                  <label style={{ fontSize:11, fontWeight:700, color:C.slate500,
                    display:'block', marginBottom:5, textTransform:'uppercase' }}>
                    Discount {discountType === 'flat' ? 'Amount (₹)' : 'Percentage (%)'}
                  </label>
                  <input className="lb-input" type="number" min="0"
                    max={discountType === 'percent' ? 100 : baseAmount}
                    placeholder={discountType === 'flat' ? 'e.g. 50' : 'e.g. 10'}
                    value={discountValue} onChange={e => setDiscountValue(e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize:11, fontWeight:700, color:C.slate500,
                    display:'block', marginBottom:5, textTransform:'uppercase' }}>
                    Reason *
                  </label>
                  <input className="lb-input" placeholder="BPL card, staff, insurance waiver…"
                    value={discountReason} onChange={e => setDiscountReason(e.target.value)} />
                </div>
              </div>
            )}
          </div>

          {/* Live preview when discount is active */}
          {discountType !== 'none' && discountValue && parseFloat(discountValue) > 0 && (
            <div style={{ background:C.greenLight, border:`1px solid ${C.greenMid}`,
              borderRadius:10, padding:'12px 16px', marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4, fontSize:13 }}>
                <span style={{ color:C.slate600 }}>Bill Amount</span>
                <span style={{ fontWeight:700, color:C.slate600, textDecoration:'line-through' }}>
                  ₹{baseAmount.toFixed(2)}
                </span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4, fontSize:13 }}>
                <span style={{ color:C.red }}>Discount</span>
                <span style={{ fontWeight:700, color:C.red }}>− ₹{discountFlat.toFixed(2)}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between',
                borderTop:`1px solid ${C.greenMid}`, paddingTop:8, marginTop:4 }}>
                <span style={{ color:C.green, fontWeight:800, fontSize:15 }}>Final Amount</span>
                <span style={{ fontFamily:C.mono, fontWeight:800, fontSize:16, color:C.green }}>
                  ₹{finalAmount.toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {error && (
            <div style={{ background:C.redLight, border:`1px solid ${C.redMid}`,
              color:C.red, padding:'9px 12px', borderRadius:8, marginBottom:14, fontSize:13 }}>
              ⚠ {error}
            </div>
          )}
        </div>

        <div style={{ padding:'16px 24px', borderTop:`1px solid ${C.slate200}`,
          display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button className="lb-btn lb-btn-outline" onClick={onClose}>Cancel</button>
          <button className="lb-btn lb-btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? '⏳ Generating…' : '🧾 Generate Bill'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Payment Confirmation Modal ───────────────────────────────────────────────
const PaymentModal = ({ order, onClose, onSuccess }) => {
  const [method,  setMethod]  = useState('Cash');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const displayAmount = parseFloat(order.final_amount || order.bill_amount || 0);
  const hasDiscount   = parseFloat(order.discount_amount || 0) > 0;

  const handleSubmit = async () => {
    setLoading(true); setError('');
    try {
      const updated = await labAPI.confirmPayment(order.id, { payment_method: method });
      onSuccess(updated);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to confirm payment');
    } finally { setLoading(false); }
  };

  return (
    <div className="lb-modal-overlay" onClick={onClose}>
      <div className="lb-modal" style={{ width: 440 }} onClick={e => e.stopPropagation()}>
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.slate200}`,
          background:'linear-gradient(135deg,#f0fdf4,#dcfce7)',
          borderRadius:'16px 16px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:C.slate900 }}>💳 Confirm Payment</div>
            <div style={{ fontSize:12, color:C.slate500, marginTop:2 }}>Order #{order.id}</div>
          </div>
          <button onClick={onClose} style={{ background:C.slate100, border:'none',
            width:32, height:32, borderRadius:'50%', cursor:'pointer', fontSize:16, color:C.slate500 }}>✕</button>
        </div>

        <div style={{ padding:24 }}>
          <div style={{ background:C.greenLight, border:`1px solid ${C.greenMid}`,
            borderRadius:10, padding:'12px 16px', marginBottom:20 }}>
            <div style={{ fontWeight:800, fontSize:15, color:'#166534' }}>{order.patient_name}</div>
            {hasDiscount && (
              <div style={{ fontSize:12, color:C.slate500, marginTop:4, textDecoration:'line-through' }}>
                Original: ₹{parseFloat(order.bill_amount || 0).toFixed(2)}
              </div>
            )}
            {hasDiscount && (
              <div style={{ fontSize:12, color:C.red, marginTop:2 }}>
                Discount: − ₹{parseFloat(order.discount_amount).toFixed(2)}
                {order.discount_reason && <span style={{ marginLeft:8, fontStyle:'italic' }}>({order.discount_reason})</span>}
              </div>
            )}
            <div style={{ marginTop: hasDiscount ? 6 : 4 }}>
              <span style={{ fontSize:13, color:'#16a34a' }}>
                {hasDiscount ? 'Final Amount' : 'Bill Amount'}:{' '}
              </span>
              <strong style={{ fontSize:16, color:'#16a34a' }}>
                ₹{displayAmount.toFixed(2)}
              </strong>
            </div>
          </div>

          {error && (
            <div style={{ background:C.redLight, border:`1px solid ${C.redMid}`,
              color:C.red, padding:'9px 12px', borderRadius:8, marginBottom:14, fontSize:13 }}>
              ⚠ {error}
            </div>
          )}

          <div>
            <label style={{ fontSize:12, fontWeight:700, color:C.slate600,
              display:'block', marginBottom:6, textTransform:'uppercase', letterSpacing:'.04em' }}>
              Payment Method
            </label>
            <select className="lb-select" style={{ width:'100%' }}
              value={method} onChange={e => setMethod(e.target.value)}>
              <option value="Cash">Cash</option>
              <option value="UPI">UPI</option>
              <option value="Card">Card (Debit / Credit)</option>
              <option value="Net Banking">Net Banking</option>
              <option value="Insurance">Insurance</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <div style={{ padding:'16px 24px', borderTop:`1px solid ${C.slate200}`,
          display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button className="lb-btn lb-btn-outline" onClick={onClose}>Cancel</button>
          <button className="lb-btn lb-btn-success" onClick={handleSubmit} disabled={loading}>
            {loading ? '⏳ Processing…' : '✅ Confirm Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const LabBilling = () => {
  const { user } = useAuth();

  const [activeTab,    setActiveTab]    = useState('pending');
  const [orders,       setOrders]       = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [searchQ,      setSearchQ]      = useState('');
  const [billModal,    setBillModal]    = useState(null);
  const [payModal,     setPayModal]     = useState(null);

  useEffect(() => { injectStyles(); }, []);

  const STATUS_MAP = {
    pending: 'ORDERED', billed: 'BILLED', paid: 'PAID', all: null,
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = STATUS_MAP[activeTab];
      const data = await labAPI.getOrders(statusParam ? { status: statusParam } : {});
      setOrders(Array.isArray(data) ? data : data.results || []);
    } catch (e) { console.error('Failed to fetch billing orders', e); }
    finally { setLoading(false); }
  }, [activeTab]); // eslint-disable-line

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const filtered = orders.filter(o => {
    if (!searchQ) return true;
    const q = searchQ.toLowerCase();
    return (
      o.patient_name?.toLowerCase().includes(q) ||
      o.patient_id?.toLowerCase().includes(q)   ||
      o.token_number?.toLowerCase().includes(q)
    );
  });

  const totalRevenue = orders
    .filter(o => ['BILLED','PAID','SAMPLE_COLLECTED','IN_PROGRESS','COMPLETED'].includes(o.status))
    .reduce((s, o) => s + parseFloat(o.final_amount || o.bill_amount || 0), 0);

  const totalDiscounts = orders
    .filter(o => parseFloat(o.discount_amount || 0) > 0)
    .reduce((s, o) => s + parseFloat(o.discount_amount || 0), 0);

  const orderCategories = (order) => {
    const cats = new Set(order.items?.map(i => i.test?.category_name).filter(Boolean));
    return [...cats];
  };

  const TABS = [
    { key:'pending', label:'🕐 Pending Orders'   },
    { key:'billed',  label:'🧾 Bill Generated'   },
    { key:'paid',    label:'✅ Payment Confirmed' },
    { key:'all',     label:'📋 All Orders'        },
  ];

  return (
    <DashboardLayout title="Lab Billing">
      <div className="lb-root" style={{ minHeight:'100vh', background:C.pageBg, padding:'24px 28px' }}>

        {/* ── Header ── */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:42, height:42, borderRadius:10, fontSize:22,
              background:'linear-gradient(135deg,#0369a1,#0d9488)',
              display:'flex', alignItems:'center', justifyContent:'center',
              boxShadow:'0 4px 12px rgba(3,105,161,.3)' }}>🧾</div>
            <div>
              <h1 style={{ margin:0, fontSize:21, fontWeight:800, color:C.slate900 }}>Lab Billing Portal</h1>
              <p style={{ margin:0, fontSize:12, color:C.textMuted }}>
                Logged in as <strong style={{ color:C.primary }}>{user?.first_name || user?.username}</strong>
              </p>
            </div>
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 }}>
          <StatCard icon="📋" label="Total in View"    value={orders.length}                              color={C.primary} />
          <StatCard icon="⏳" label="Pending Billing"  value={orders.filter(o=>o.status==='ORDERED').length} color={C.orange} />
          <StatCard icon="🧾" label="Billed"           value={orders.filter(o=>o.status==='BILLED').length}  color={C.blue} />
          <StatCard icon="💰" label="Revenue (₹)"      value={totalRevenue.toFixed(0)}                    color={C.green} />
        </div>

        {/* Discounts notice if any */}
        {totalDiscounts > 0 && (
          <div style={{ background:C.yellowLight, border:`1px solid ${C.yellowMid}`,
            borderRadius:10, padding:'10px 16px', marginBottom:16, display:'flex',
            alignItems:'center', gap:10, fontSize:13 }}>
            <span>🏷️</span>
            <span style={{ color:C.yellow }}>
              <strong>₹{totalDiscounts.toFixed(2)}</strong> in discounts applied across{' '}
              <strong>{orders.filter(o => parseFloat(o.discount_amount || 0) > 0).length}</strong> orders.
            </span>
          </div>
        )}

        {/* ── Tab Switcher ── */}
        <div style={{ display:'flex', gap:4, marginBottom:20, background:C.slate200,
          borderRadius:12, padding:4 }}>
          {TABS.map(t => (
            <button key={t.key}
              className={`lb-tab-btn ${activeTab===t.key ? 'active' : 'inactive'}`}
              onClick={() => { setActiveTab(t.key); setSearchQ(''); }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Main Card ── */}
        <div style={{ background:'#fff', borderRadius:14, border:`1px solid ${C.slate200}`,
          boxShadow:'0 2px 12px rgba(0,0,0,.06)', overflow:'hidden' }}>

          <div style={{ padding:'16px 24px', borderBottom:`1px solid ${C.slate200}`,
            display:'flex', alignItems:'center', justifyContent:'space-between', background:C.slate50 }}>
            <div style={{ fontSize:14, fontWeight:700, color:C.slate900 }}>
              {TABS.find(t=>t.key===activeTab)?.label} &nbsp;
              <span style={{ fontSize:12, fontWeight:400, color:C.textMuted }}>({filtered.length} records)</span>
            </div>
            <input className="lb-input" style={{ width:280 }}
              placeholder="Search patient, ID, token…"
              value={searchQ} onChange={e => setSearchQ(e.target.value)} />
          </div>

          {loading ? (
            <div style={{ padding:56, textAlign:'center', color:C.textMuted }}>
              <div style={{ fontSize:32, marginBottom:12 }}>⏳</div>
              <div style={{ fontWeight:600 }}>Loading billing orders…</div>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding:56, textAlign:'center', color:C.textMuted }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🧾</div>
              <div style={{ fontWeight:700, fontSize:15 }}>
                {searchQ ? 'No results found' : 'No orders in this category'}
              </div>
            </div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr>
                    {['Patient', 'Token', 'Tests / Categories', 'Urgency', 'Bill Amt', 'Discount', 'Final', 'Status', 'Actions'].map(h => (
                      <th key={h} style={thS}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(order => {
                    const cats = orderCategories(order);
                    const urgencyColors = {
                      STAT: { bg:'#fee2e2', color:'#991b1b' },
                      URGENT: { bg:'#fef9c3', color:'#854d0e' },
                      ROUTINE: { bg:'#f1f5f9', color:'#475569' },
                    };
                    const uc = urgencyColors[order.urgency] || urgencyColors.ROUTINE;
                    const hasDiscount = parseFloat(order.discount_amount || 0) > 0;
                    return (
                      <tr key={order.id} className="lb-tr">
                        <td style={tdS}>
                          <div style={{ fontWeight:700, fontSize:14 }}>{order.patient_name}</div>
                          <div style={{ fontSize:11, color:C.textMuted, marginTop:2 }}>{order.patient_id}</div>
                        </td>
                        <td style={tdS}>
                          <span style={{ fontFamily:C.mono, fontWeight:700, color:C.primary }}>
                            {order.token_number || '—'}
                          </span>
                        </td>
                        <td style={{ ...tdS, maxWidth:240 }}>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:4 }}>
                            {cats.map(c => <CategoryTag key={c} name={c} />)}
                          </div>
                          <div style={{ fontSize:11, color:C.slate500, lineHeight:1.5 }}>
                            {order.items?.map(i => i.test_name).join(' · ')}
                          </div>
                        </td>
                        <td style={tdS}>
                          <span style={{ padding:'3px 9px', borderRadius:6, fontSize:11,
                            fontWeight:800, background:uc.bg, color:uc.color }}>
                            {order.urgency}
                          </span>
                        </td>
                        <td style={tdS}>
                          {order.bill_amount
                            ? <span style={{ fontFamily:C.mono, color:C.textSecondary,
                                textDecoration: hasDiscount ? 'line-through' : 'none',
                                fontSize: hasDiscount ? 11 : 13 }}>
                                ₹{parseFloat(order.bill_amount).toFixed(2)}
                              </span>
                            : <span style={{ color:C.textMuted }}>—</span>}
                        </td>
                        <td style={tdS}>
                          {hasDiscount ? (
                            <div>
                              <span style={{ color:C.yellow, fontFamily:C.mono, fontWeight:700, fontSize:12 }}>
                                − ₹{parseFloat(order.discount_amount).toFixed(2)}
                              </span>
                              {order.discount_reason && (
                                <div style={{ fontSize:10, color:C.textMuted, maxWidth:100, lineHeight:1.3, marginTop:2 }}>
                                  {order.discount_reason}
                                </div>
                              )}
                            </div>
                          ) : <span style={{ color:C.textMuted }}>—</span>}
                        </td>
                        <td style={tdS}>
                          {order.bill_amount
                            ? <strong style={{ color:C.tealDark, fontFamily:C.mono }}>
                                ₹{parseFloat(order.final_amount || order.bill_amount).toFixed(2)}
                              </strong>
                            : <span style={{ color:C.textMuted }}>—</span>}
                        </td>
                        <td style={tdS}><Badge status={order.status} /></td>
                        <td style={{ ...tdS, whiteSpace:'nowrap' }}>
                          <div style={{ display:'flex', gap:6 }}>
                            {order.status === 'ORDERED' && (
                              <button className="lb-btn lb-btn-primary"
                                style={{ padding:'6px 12px', fontSize:12 }}
                                onClick={() => setBillModal(order)}>
                                🧾 Generate Bill
                              </button>
                            )}
                            {order.status === 'BILLED' && (
                              <>
                                <button className="lb-btn lb-btn-success"
                                  style={{ padding:'6px 12px', fontSize:12 }}
                                  onClick={() => setPayModal(order)}>
                                  💳 Confirm Payment
                                </button>
                                <button className="lb-btn lb-btn-outline"
                                  style={{ padding:'6px 10px', fontSize:12 }}
                                  title="Print Invoice"
                                  onClick={() => printInvoice(order)}>
                                  🖨
                                </button>
                              </>
                            )}
                            {order.status === 'PAID' && (
                              <>
                                <span style={{ fontSize:12, color:C.green, fontWeight:700, alignSelf:'center' }}>✅ Paid</span>
                                <button className="lb-btn lb-btn-outline"
                                  style={{ padding:'6px 10px', fontSize:11 }}
                                  title="Print Invoice"
                                  onClick={() => printInvoice(order)}>
                                  🖨 Invoice
                                </button>
                              </>
                            )}
                            {['SAMPLE_COLLECTED','IN_PROGRESS','COMPLETED'].includes(order.status) && (
                              <>
                                <span style={{ fontSize:12, color:C.slate500, fontWeight:600, alignSelf:'center' }}>In Lab</span>
                                <button className="lb-btn lb-btn-outline"
                                  style={{ padding:'6px 10px', fontSize:11 }}
                                  title="Print Invoice"
                                  onClick={() => printInvoice(order)}>
                                  🖨 Invoice
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {billModal && (
        <BillModal order={billModal} onClose={() => setBillModal(null)}
          onSuccess={() => { setBillModal(null); fetchOrders(); }} />
      )}
      {payModal && (
        <PaymentModal order={payModal} onClose={() => setPayModal(null)}
          onSuccess={() => { setPayModal(null); fetchOrders(); }} />
      )}
    </DashboardLayout>
  );
};

export default LabBilling;